import * as React from 'react';

interface ResizeHandleProps {
    isRound: boolean;
    left?: string | number;
    top?: string | number;
    bottom?: string | number;
    right?: string | number;
    pos: string;
    onDrag(dx: number, dy: number, pos: string): any;
    onDragStart?(): void;
    onDragEnd?(): void;
}

interface ResizeHandleState {
    dragging: boolean;
    active: boolean;
}

export default class ResizeHandle extends React.Component<ResizeHandleProps, ResizeHandleState> {
    private handle: HTMLElement;
    private dragRaf: number;

    componentWillMount() {
        this.setActiveState(false);
    }

    componentDidMount() {
        let delay = 0;
        switch (this.props.pos.toLowerCase()) {
            case "se":
            delay = 100;
            break;
            case "sw":
            delay = 300;
            break;
            case "s":
            delay = 500;
            break;
            case "e":
            delay = 700;
            break;
            case "w":
            delay = 900;
            break;
            default:
            delay = 0;
            break;
        }
        setTimeout(() => {
            if (this.handle && this.handle.style) {
                this.handle.style.opacity = "";
            }
        }, delay);
    }

    componentWillUnmount() {
        this.removeDragEvents();
        this.removeTouchDragEvents();
        if (this.dragRaf) {
            cancelAnimationFrame(this.dragRaf);
        }
    }

    render() {
        return (
            <div
                data-test-id={`resize-handle-${this.props.pos}`}
                ref={(ref) => this.handle = ref}
                style={this.getStyle()}
                onMouseDown={this.handleMouseDown}
                onMouseOver={this.handleMouseOver}
                onMouseOut={this.handleMouseOut}
                onTouchStart={this.handleTouchStart}
            />
        );
    }

    private handleMouseDown = (e: React.MouseEvent<{}>) => {
        e.preventDefault();
        this.addDragEvents();
        this.startDrag();
    };

    private handleTouchStart = (e: React.TouchEvent<{}>) => {
        e.preventDefault();
        this.addTouchDragEvents();
        this.startDrag();
    };

    private handleDrag = (e: MouseEvent) => {
        e.preventDefault();
        this.drag(e.clientX, e.clientY);
    };

    private handleDragEnd = () => {
        this.removeDragEvents();
        this.stopDrag();
    };

    private handleTouchDragEnd = () => {
        this.removeTouchDragEvents();
        this.stopDrag();
    };

    private handleTouchDrag = (e: TouchEvent) => {
        this.drag(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
    };

    private handleMouseOver = () => {
        if (!this.state.dragging) {
            this.setActiveState(true);
        }
    };

    private handleMouseOut = () => {
        if (!this.state.dragging) {
            this.setActiveState(false);
        }
    };

    private drag(x: number, y: number) {
        if (this.dragRaf) {
            cancelAnimationFrame(this.dragRaf);
        }
        this.dragRaf = requestAnimationFrame(() => {
            const box = this.handle.getBoundingClientRect();
            this.props.onDrag(
                x - (box.left + box.width / 2),
                y - (box.top + box.height / 2),
                this.props.pos
            );
            this.dragRaf = null;
        });
    }

    private startDrag() {
        this.setState({
            dragging: true,
            active: true
        });
        if (this.props.onDragStart) {
            this.props.onDragStart();
        }
    }

    private stopDrag() {
        this.setState({
            dragging: false,
            active: false
        });
        if (this.props.onDragEnd) {
            this.props.onDragEnd();
        }
    }

    private addDragEvents() {
        document.addEventListener('mousemove', this.handleDrag, false);
        document.addEventListener('mouseup', this.handleDragEnd, false);
    }

    private addTouchDragEvents() {
        document.addEventListener('touchmove', this.handleTouchDrag, false);
        document.addEventListener('touchend', this.handleTouchDragEnd, false);
        document.addEventListener('touchup', this.handleTouchDragEnd, false);
        document.addEventListener('touchcancel', this.handleTouchDragEnd, false);
    }

    private removeDragEvents() {
        document.removeEventListener('mousemove', this.handleDrag, false);
        document.removeEventListener('mouseup', this.handleDragEnd, false);
    }

    private removeTouchDragEvents() {
        document.removeEventListener('touchmove', this.handleTouchDrag, false);
        document.removeEventListener('touchend', this.handleTouchDragEnd, false);
        document.removeEventListener('touchup', this.handleTouchDragEnd, false);
        document.removeEventListener('touchcancel', this.handleTouchDragEnd, false);
    }

    private setActiveState(status: boolean) {
        let newState = {} as ResizeHandleState;
        newState.active = status;
        this.setState(newState);
    }

    private getStyle() {
        const translateX = this.props.left === undefined ? "50%" : "-50%";
        const translateY = this.props.top === undefined ? "50%" : "-50%";
        const pos = this.props.pos.toLowerCase();
        let cursor = "";

        switch (pos) {
            case "w":
            cursor = "ew-resize";
            break;
            case "sw":
            cursor = "nesw-resize";
            break;
            case "s":
            cursor = "ns-resize";
            break;
            case "se":
            cursor = "nwse-resize";
            break;
            case "e":
            cursor = "ew-resize";
            break;
            default:
            cursor = "auto";
            break;
        }

        return {
            background: "white",
            backgroundClip: "padding-box",
            borderStyle: "solid",
            borderWidth: this.state.active ? "8px" : "3px",
            borderColor: this.state.active ? "rgba(33, 150, 243, 0.6)" : "#2196f3",
            borderRadius: this.props.isRound ? "50%" : 0,
            cursor: this.state.active ? cursor : "auto",
            height: 18,
            width: 18,
            pointerEvents: "all",
            position: "absolute",
            transform: `translate(${translateX}, ${translateY})`,
            transition: `border 0.15s ease-in, opacity 0.3s ease-out`,
            zIndex: 100055,
            top: this.props.top,
            left: this.props.left,
            bottom: this.props.bottom,
            right: this.props.right,
            opacity: 0
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/components/ResizeHandle.tsx
 **/